import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { convertTimezone, resolveJobTimeZone } from '@s20/common/util/date';
import { ChargeTypeResourceTypes } from '../../Common';

export const WEEKDAY_FIELDS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
export const NAVIGATION_ACTIONS = { PREVIOUS: 'PREV', NEXT: 'NEXT', TODAY: 'TODAY' };

export const MAX_EVENTS_PER_DAY = 2;

export const updateResources = (resources, targetDate, preserveIds = false) => {
    const targetDayIndex = moment(targetDate).day();
    const targetDayField = WEEKDAY_FIELDS[targetDayIndex];
    const iso = moment(targetDate).format('YYYY-MM-DDTHH:mm:ss');

    return (resources || []).map((resource) => {
        const isRental = resource.jobResourceTypeId === ChargeTypeResourceTypes.Rental;

        const clone = {
            ...resource,
            id: preserveIds ? resource.id : null,
            jobId: preserveIds ? resource.jobId : 0,
            monday: 0,
            tuesday: 0,
            wednesday: 0,
            thursday: 0,
            friday: 0,
            saturday: 0,
            sunday: 0,
        };

        WEEKDAY_FIELDS.forEach((d) => {
            clone[d] = 0;
        });

        if (isRental) {
            const drop = resource.rentalDropoffQuantity || 0;
            const pick = resource.rentalPickupQuantity || 0;

            clone[targetDayField] = (drop > 0 || pick > 0) ? 1 : 0;
            clone.rentalDropoffQuantity = drop;
            clone.rentalPickupQuantity = pick;
            clone.rentalStartDate = drop > 0 ? iso : null;
            clone.rentalDropoffDate = drop > 0 ? iso : null;
            clone.rentalEndDate = pick > 0 ? iso : null;
            clone.rentalPickupDate = pick > 0 ? iso : null;
            clone.rentalDropoffDateOffset = null;
            clone.rentalPickupDateOffset = null;
            clone.rentalStartDateOffset = null;
            clone.rentalEndDateOffset = null;

            return clone;
        }

        const sourceDayField = WEEKDAY_FIELDS.find((day) => (resource[day] || 0) > 0) || WEEKDAY_FIELDS[0];
        const quantity = Number(resource[sourceDayField]) || 0;
        clone[targetDayField] = Math.max(0, quantity);

        return clone;
    });
};

export const createJobCopyDto = (sourceJob, targetDate, dispatchLocations) => {
    const newJobId = uuid();

    // The source job's wall clock at the job site, placed on the target day and saved in the
    // job's zone.
    const source = moment.parseZone(sourceJob.startTimeOffset);
    const start = moment(targetDate).hour(source.hour()).minute(source.minute()).second(0).millisecond(0);
    const targetMoment = convertTimezone(start, resolveJobTimeZone(sourceJob, dispatchLocations));

    return {
        weekOfDate: moment(targetDate).startOf('isoWeek').format('YYYY-MM-DD'),
        foremenId: sourceJob.foremenId,
        requestedById: sourceJob.requestedById,
        projectManagerId: sourceJob.projectManagerId,
        projectName: sourceJob.projectName,
        subcontractorCompanyId: sourceJob.subcontractorCompanyId,
        meetingLocation: sourceJob.meetingLocation,
        jobLocation: sourceJob.jobLocation,
        id: null,
        tempSessionId: newJobId,
        contract: sourceJob.contract,
        contractId: sourceJob.contractId,
        description: sourceJob.description,
        workOrderNumber: sourceJob.workOrderNumber,
        purchaseOrderNumber: sourceJob.purchaseOrderNumber,
        workRequest: sourceJob.workRequest,
        dispatchOfficeLocationId: sourceJob.dispatchOfficeLocationId,
        dispatchPortId: sourceJob.dispatchPortId ?? null,
        notes: sourceJob.notes,
        inspectorName: sourceJob.inspectorName,
        jobTypeId: sourceJob.jobTypeId,
        projectId: sourceJob.projectId,
        parentJobId: sourceJob.parentJobId || sourceJob.id,
        startTime: targetMoment,
        startTimeOffset: targetMoment,
        jobDate: targetMoment,
        resources: updateResources(sourceJob.resources, targetDate),
    };
};